// ─────────────────────────────────────────────
//  linkProviderChains — auto-route any provider scheduled in MULTIPLE rooms.
//  Rooms are ordered by start time: the earliest is their first case, each
//  later one the next. Consecutive rooms get the existing Next Room link
//  (nextRoomId/nextRoomName on the earlier row), so the board shows
//  "Next Room: X" on the earlier card and "Coming from: Y" on the later
//  card — the same bidirectional display the manual ➜ button uses.
//
//  Three passes, in priority order (one link per row — first pass wins):
//    1. CRNA/AA (ahpId)      — always chained by start time; the CRNA is
//                              physically in one room at a time.
//    2. Attending (attendingId) — chained ONLY when the earlier case ENDS
//                              before the later STARTS. Supervisors cover
//                              several rooms in parallel; overlap ≠ a route.
//    3. Surgeon (name)       — same non-overlap rule.
//
//  Pure. Input rows are draft-shaped ({ orRoom, ahpId, attendingId, surgeon,
//  startTime, endTime, … }); `rooms` are the facility's live room docs (to
//  resolve the destination room id the incoming-arrow derivation matches on).
// ─────────────────────────────────────────────
import { roomForOrRoom } from "../../domain/gridTemplate";
import { resolveRoomHint } from "../../domain/roomResolver";
import { qualifyHint } from "../../domain/roomCampus";

const normName = (v) => String(v || "").trim().toLowerCase().replace(/[^a-z]/g, "");

// Match a sheet routing hint ("CT", "TOR16", "CIE6") to a draft row's room —
// tolerant of the missing space ("TOR16" vs "TOR 16") and case.
const roomKey = (v) => String(v || "").toUpperCase().replace(/\s+/g, "");

export function linkProviderChains(draftRows, rooms = []) {
  const out = (Array.isArray(draftRows) ? draftRows : []).map((r) => ({ ...r }));

  // Pass 0 — the sheet's OWN arrows: a row whose AHP cell said "to X" (or a
  // bare trailing room token) links straight to that room. Highest priority —
  // the sheet author knows the route better than the time heuristic.
  //
  // ⚠️ THE ARROWS ARE WRITTEN IN THE LOCAL DIALECT, NOT IN ROOM-DOC NAMES.
  // A Wesley Chapel row says "to TEE"; the room doc is "WC TEE". A Carrollwood
  // row says "to OR 6"; the room doc is "COR 6". Matching on a squashed string
  // alone resolved exactly ONE of the three arrows in the measured exports —
  // the one happening to be written in Tampa's bare dialect. `qualifyHint`
  // re-reads the arrow in the campus of the row it was written on, ALWAYS
  // trying the verbatim text first so an arrow that already names a real room
  // is never rewritten.
  const hintTargets = (r, hint) => {
    const tried = qualifyHint(hint, r.orRoom);
    for (const cand of tried) {
      const k = roomKey(cand);
      if (!k) continue;
      const row = out.find((x) => x !== r && roomKey(x.orRoom) === k);
      if (row) return { row, room: null };
    }
    const v = resolveRoomHint(hint, rooms, { contextLabel: r.orRoom, qualify: qualifyHint });
    return v.status === "resolved" ? { row: null, room: v.room } : null;
  };

  for (const r of out) {
    if (!r || r.nextRoomName || !r.toRoomHint) continue;
    const targetKey = roomKey(r.toRoomHint);
    if (!targetKey || targetKey === roomKey(r.orRoom)) continue; // self-reference
    const hit = hintTargets(r, r.toRoomHint);
    if (hit) {
      r.nextRoomName = hit.row?.orRoom || hit.room?.name || r.toRoomHint;
      r.nextRoomId = hit.room?.id || roomForOrRoom(r.nextRoomName, rooms)?.id || null;
    }
  }

  // Pass 0b — the arrows pointing the OTHER way. "Bill Layton 7 from OR 10" on
  // the WOR 5 row says Layton's OR 10 case is followed by this one. That is the
  // same fact as a "to" arrow on the OR 10 row, and until now it was parsed,
  // written to `fromRoomHint`, and read by NOBODY — every incoming arrow on the
  // sheet thrown away.
  //
  // ⚠️ IT NEVER OVERRIDES AN EXPLICIT ROOM. It only fills a source row that has
  // no link yet, never closes a loop, and never links a row to itself.
  for (const r of out) {
    if (!r || !r.fromRoomHint) continue;
    const selfKey = roomKey(r.orRoom);
    const hit = hintTargets(r, r.fromRoomHint);
    const source = hit?.row;
    if (!source || source.nextRoomName) continue; // explicit link wins
    if (roomKey(source.orRoom) === selfKey) continue;
    if (roomKey(r.nextRoomName) === roomKey(source.orRoom)) continue; // would close a loop
    source.nextRoomName = r.orRoom || "";
    source.nextRoomId = roomForOrRoom(r.orRoom, rooms)?.id || null;
  }

  const groupBy = (keyFn) => {
    const map = new Map();
    out.forEach((r, i) => {
      if (!r || !String(r.orRoom || "").trim()) return;
      const key = keyFn(r);
      if (!key) return;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(i);
    });
    return map;
  };

  const chain = (idxs, { requireNonOverlap }) => {
    if (idxs.length < 2) return;
    // Earliest start first; rows with no time sort last (they can still be a
    // chain's final stop, but never claim to be someone's first case).
    const sorted = [...idxs].sort((a, b) =>
      String(out[a].startTime || "99:99").localeCompare(String(out[b].startTime || "99:99"))
    );
    for (let k = 0; k < sorted.length - 1; k += 1) {
      const cur = out[sorted[k]];
      const next = out[sorted[k + 1]];
      if (cur.nextRoomName) continue; // one link per row — earlier pass wins
      // Never close a loop: if the destination already routes back to us
      // (e.g. a sheet-hint link the other way), a reciprocal link is noise.
      if (next.nextRoomName && roomKey(next.nextRoomName) === roomKey(cur.orRoom)) continue;
      if (requireNonOverlap) {
        if (!cur.endTime || !next.startTime) continue;
        if (String(cur.endTime) > String(next.startTime)) continue; // parallel coverage
      }
      cur.nextRoomName = next.orRoom || "";
      cur.nextRoomId = roomForOrRoom(next.orRoom, rooms)?.id || null;
    }
  };

  for (const idxs of groupBy((r) => r.ahpId).values()) chain(idxs, { requireNonOverlap: false });
  for (const idxs of groupBy((r) => r.attendingId).values()) chain(idxs, { requireNonOverlap: true });
  for (const idxs of groupBy((r) => normName(r.surgeon)).values()) chain(idxs, { requireNonOverlap: true });

  return out;
}

// Back-compat alias — the provider-chain pass is a strict superset of the
// original CRNA-only behavior.
export const linkCrnaChains = linkProviderChains;
